import { supabase } from '../supabase';
import { validateWhatsApp } from '../phone-utils';

export async function signUp(
  whatsapp: string, 
  password: string, 
  name: string,
  referralCode?: string
) {
  console.log('Starting signup process with params:', {
    whatsapp,
    name,
    hasReferralCode: !!referralCode
  });

  if (!validateWhatsApp(whatsapp)) {
    console.error('Invalid WhatsApp format:', whatsapp);
    throw new Error('Número de WhatsApp inválido');
  }

  // Remove all non-digits and ensure it has 11 digits
  const digits = whatsapp.replace(/\D/g, '');
  if (digits.length !== 11) {
    console.error('WhatsApp must have 11 digits:', digits.length);
    throw new Error('O número deve ter 11 dígitos (DDD + número)');
  }

  try {
    // If referral code provided, validate it first
    if (referralCode) {
      console.log('Validating referral code:', referralCode);
      
      const { data: referralData, error: referralError } = await supabase
        .from('referral_codes')
        .select('code, user_id')
        .eq('code', referralCode)
        .maybeSingle();

      if (referralError) {
        console.error('Error checking referral code:', referralError);
        throw new Error('Erro ao verificar código de indicação');
      }

      if (!referralData) {
        console.error('Invalid referral code:', referralCode);
        throw new Error('Código de indicação inválido');
      }

      console.log('Referral code validated:', {
        code: referralData.code,
        referrerId: referralData.user_id
      });
    }

    // Format email consistently for authentication
    const email = `${digits}@whatsapp.user`;

    // Create metadata object
    const metadata = {
      sub: crypto.randomUUID(),
      name,
      block: false,
      email,
      whatsapp: digits,
      created_at: new Date().toISOString(),
      tipo_usuario: 'cliente',
      email_verified: true,
      phone_verified: false
    };

    console.log('Creating new user with metadata:', metadata);
    
    // Create new user with metadata
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });

    if (signUpError) {
      console.error('Signup error:', {
        error: signUpError,
        code: signUpError.status,
        message: signUpError.message
      });
      
      if (signUpError.message.includes('User already registered')) {
        throw new Error('Este número de WhatsApp já está cadastrado');
      }
      
      throw signUpError;
    }

    if (!data.user) {
      console.error('No user data returned from signup');
      throw new Error('Erro ao criar usuário');
    }

    // If referral code was provided and valid, create referral record
    if (referralCode) {
      console.log('Creating referral record:', {
        userId: data.user.id,
        referralCode
      });

      const { error: referralError } = await supabase
        .from('user_referrals')
        .insert({
          referral_code: referralCode,
          referred_user_id: data.user.id
        });

      if (referralError) {
        console.error('Error creating referral record:', {
          error: referralError,
          userId: data.user.id,
          referralCode
        });
      } else {
        console.log('Referral record created successfully');
      }
    }

    console.log('User created successfully:', {
      userId: data.user.id,
      email: data.user.email,
      metadata: data.user.user_metadata,
      hasReferral: !!referralCode
    });

    return data;
  } catch (err) {
    console.error('Signup process failed:', {
      error: err,
      message: err instanceof Error ? err.message : 'Unknown error',
      stack: err instanceof Error ? err.stack : undefined
    });
    throw err instanceof Error ? err : new Error('Erro ao criar conta');
  }
}